import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { GatewayRulesFormValues } from 'pages/GatewayRules/components/GatewayRulesForm/hooks/useGatewayRulesForm';
import { useGetList, useGetOne } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { cleanEmpty } from 'utils/cleanEmpty';

type Params = {
  mappingIndex: number;
  mappingItemIndex: number;
};

export function useMerchantAccounts({ mappingIndex, mappingItemIndex }: Params) {
  const { control } = useFormContext<GatewayRulesFormValues>();

  const merchantWatched = useWatch({ control, name: 'merchant' });
  const gatewayWatched = useWatch({
    control,
    name: `mappings.${mappingIndex}.mappingItems.${mappingItemIndex}.gateway`,
  });
  const accountWatched = useWatch({
    control,
    name: `mappings.${mappingIndex}.mappingItems.${mappingItemIndex}.merchantAccount`,
  });

  // один список вместо ReferenceInput + списка в автовыборе
  const { data: accounts = [], isLoading: isLoadingAccounts } = useGetList<MerchantAccount>(
    Resources.MerchantAccounts,
    {
      pagination: { page: 1, perPage: 200 },
      filter: cleanEmpty({
        groups: ['reference'],
        gateways: gatewayWatched,
        merchants: merchantWatched,
        active: 1,
      }),
    },
    { enabled: Boolean(gatewayWatched && merchantWatched) },
  );

  // currency в группе reference нет, поэтому выбранный аккаунт грузим отдельно
  const { data: selectedAccount, isLoading: isLoadingSelectedAccount } = useGetOne<MerchantAccount>(
    Resources.MerchantAccounts,
    { id: accountWatched },
    { enabled: Boolean(accountWatched) },
  );

  // выбранный аккаунт мог не попасть в список
  const accountChoices = useMemo(
    () =>
      selectedAccount && !accounts.some((a) => a.id === selectedAccount.id)
        ? [selectedAccount, ...accounts]
        : accounts,
    [accounts, selectedAccount],
  );

  return {
    accounts,
    accountChoices,
    accountWatched,
    currency: selectedAccount?.currency,
    isLoadingAccounts: isLoadingAccounts || isLoadingSelectedAccount,
  };
}
